import React, { useState, useEffect, useCallback } from 'react';
import { maintenancePlansAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import {
  Plus, Loader2, Edit2, Users, Calendar, DollarSign, Clock,
  Wrench, Pause, XCircle, Play, ChevronRight, RefreshCw, ArrowUpRight, Trash2, AlertTriangle, UserMinus, ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { MobileFAB } from '../components/MobileFAB';

const FREQ_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
};

const formatPlanTime12h = (hhmm) => {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const STATUS_STYLES = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800',
  paused: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800',
  canceled: 'bg-stone-100 text-stone-500 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700',
};

function PlanCard({ plan, onEdit, onViewEnrollments, onDelete }) {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`plan-card-${plan.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground truncate">{plan.name}</h3>
              {!plan.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
            </div>
            {plan.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{plan.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5" />
                ${plan.price.toFixed(2)} / {FREQ_LABELS[plan.billing_frequency]?.toLowerCase() || plan.billing_frequency}
              </span>
              <span className="flex items-center gap-1">
                <Wrench className="w-3.5 h-3.5" />
                {plan.visits_per_year} visit{plan.visits_per_year !== 1 ? 's' : ''}/year
              </span>
              <span className="flex items-center gap-1" data-testid={`plan-time-${plan.id}`}>
                <Clock className="w-3.5 h-3.5" />
                {formatPlanTime12h(plan.default_scheduled_time) || '8:00 AM'}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {plan.enrollment_count} enrolled
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => onEdit(plan)} data-testid={`edit-plan-${plan.id}`}>
              <Edit2 className="w-4 h-4" />
            </Button>
            {plan.enrollment_count > 0 && (
              <Button variant="ghost" size="sm" onClick={() => onViewEnrollments(plan)} data-testid={`view-enrollments-${plan.id}`}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => onDelete(plan)} data-testid={`delete-plan-${plan.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanForm({ plan, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    price: plan?.price?.toString() || '',
    billing_frequency: plan?.billing_frequency || 'monthly',
    visits_per_year: plan?.visits_per_year?.toString() || '2',
    default_scheduled_time: plan?.default_scheduled_time || '08:00',
    is_active: plan?.is_active ?? true,
  });

  // Format HH:MM (24h) → "8:00 AM"
  const formatTime12h = (hhmm) => {
    if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return '';
    const [h, m] = hhmm.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return toast.error('Plan name is required');
    const price = parseFloat(form.price);
    if (isNaN(price) || price <= 0) return toast.error('Price must be greater than 0');
    const visits = parseInt(form.visits_per_year);
    if (isNaN(visits) || visits < 1) return toast.error('Visits per year must be at least 1');
    const timeStr = form.default_scheduled_time || '08:00';
    if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(timeStr)) return toast.error('Scheduled time must be HH:MM');
    onSave({
      name: form.name.trim(),
      description: form.description.trim() || null,
      price,
      billing_frequency: form.billing_frequency,
      visits_per_year: visits,
      default_scheduled_time: timeStr,
      is_active: form.is_active,
    });
  };

  return (
    <div className="space-y-4 p-1">
      <div className="space-y-2">
        <Label>Plan Name</Label>
        <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Gold HVAC Plan" data-testid="plan-name-input" />
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What's included in this plan..." rows={3} data-testid="plan-description-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Price per Cycle ($)</Label>
          <Input type="number" min="0.01" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="49.99" data-testid="plan-price-input" />
        </div>
        <div className="space-y-2">
          <Label>Billing Frequency</Label>
          <Select value={form.billing_frequency} onValueChange={v => setForm(f => ({ ...f, billing_frequency: v }))}>
            <SelectTrigger data-testid="plan-frequency-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="semi_annual">Semi-Annual</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Visits per Year</Label>
          <Input type="number" min="1" max="52" value={form.visits_per_year} onChange={e => setForm(f => ({ ...f, visits_per_year: e.target.value }))} data-testid="plan-visits-input" />
          <p className="text-xs text-muted-foreground">How many service jobs to auto-schedule per year</p>
        </div>
        <div className="space-y-2">
          <Label>Default Scheduled Time</Label>
          <Input
            type="time"
            step="300"
            value={form.default_scheduled_time}
            onChange={e => setForm(f => ({ ...f, default_scheduled_time: e.target.value }))}
            data-testid="plan-scheduled-time-input"
          />
          <p className="text-xs text-muted-foreground">
            Jobs will be scheduled at <span className="font-medium text-foreground" data-testid="plan-scheduled-time-preview">{formatTime12h(form.default_scheduled_time) || '—'}</span> local time
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="save-plan-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {plan ? 'Update Plan' : 'Create Plan'}
        </Button>
      </div>
    </div>
  );
}

const SOURCE_LABELS = { manual: 'Manual', upsell: 'Upsell', import: 'Import' };

function EnrollmentRow({ enrollment, onAction }) {
  const startFormatted = enrollment.start_date ? new Date(enrollment.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const billFormatted = enrollment.next_billing_date ? new Date(enrollment.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
  const svcFormatted = enrollment.next_service_date ? new Date(enrollment.next_service_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;

  return (
    <div className="flex items-center justify-between py-4 px-5 border-b last:border-b-0 hover:bg-muted/60 transition-colors" data-testid={`enrollment-${enrollment.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm text-foreground truncate">{enrollment.customer_name || 'Unknown'}</span>
          <span className="text-muted-foreground/40 font-light">/</span>
          <span className="font-medium text-sm text-foreground/80 truncate">{enrollment.plan_name}</span>
          <Badge variant="outline" className={`text-xs ${STATUS_STYLES[enrollment.status] || ''}`}>
            {enrollment.status}
          </Badge>
          {enrollment.source && (
            <Badge variant="outline" className={`text-xs ${enrollment.source === 'upsell' ? 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800' : ''}`}>
              {enrollment.source === 'upsell' && <ArrowUpRight className="w-3 h-3 mr-0.5" />}
              {SOURCE_LABELS[enrollment.source] || enrollment.source}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">${enrollment.enrolled_price.toFixed(2)}</span>
          {startFormatted && <span>Since {startFormatted}</span>}
          {(billFormatted || svcFormatted) && <span className="text-muted-foreground/40">|</span>}
          {billFormatted && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Next bill: {billFormatted}</span>}
          {billFormatted && svcFormatted && <span className="text-muted-foreground/40">&bull;</span>}
          {svcFormatted && <span className="flex items-center gap-1"><Wrench className="w-3 h-3" /> Next service: {svcFormatted}</span>}
          <span className="text-muted-foreground/40">|</span>
          <span className="flex items-center gap-1" data-testid={`enrollment-row-time-${enrollment.id}`}>
            <Clock className="w-3 h-3" />
            {formatPlanTime12h(enrollment.effective_scheduled_time) || '8:00 AM'}
            {enrollment.scheduled_time_override && (
              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800 h-4 px-1 ml-0.5">
                Override
              </Badge>
            )}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {enrollment.status === 'active' && (
          <>
            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => onAction(enrollment.id, 'paused')} data-testid={`pause-enrollment-${enrollment.id}`}>
              <Pause className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => onAction(enrollment.id, 'canceled')} data-testid={`cancel-enrollment-${enrollment.id}`}>
              <XCircle className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {enrollment.status === 'paused' && (
          <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => onAction(enrollment.id, 'active')} data-testid={`resume-enrollment-${enrollment.id}`}>
            <Play className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function MaintenancePlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Delete flow state
  const [deleteTarget, setDeleteTarget] = useState(null); // plan to delete
  const [deleteAction, setDeleteAction] = useState('remove'); // 'remove' | 'reassign'
  const [reassignTarget, setReassignTarget] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadPlans = useCallback(async () => {
    try {
      const res = await maintenancePlansAPI.list(!showInactive);
      setPlans(res.data.plans);
    } catch { toast.error('Failed to load plans'); }
  }, [showInactive]);

  const loadEnrollments = useCallback(async () => {
    try {
      const res = await maintenancePlansAPI.allEnrollments({});
      setEnrollments(res.data.enrollments);
    } catch { toast.error('Failed to load enrollments'); }
  }, []);

  useEffect(() => {
    Promise.all([loadPlans(), loadEnrollments()]).finally(() => setLoading(false));
  }, [loadPlans, loadEnrollments]);

  const handleSavePlan = async (data) => {
    setSaving(true);
    try {
      if (editingPlan) {
        await maintenancePlansAPI.update(editingPlan.id, data);
        toast.success('Plan updated');
      } else {
        await maintenancePlansAPI.create(data);
        toast.success('Plan created');
      }
      setDrawerOpen(false);
      setEditingPlan(null);
      await loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    } finally { setSaving(false); }
  };

  const handleDeletePlan = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.enrollment_count > 0) {
        await maintenancePlansAPI.deletePlanWithEnrollments(deleteTarget.id, {
          action: deleteAction,
          target_plan_id: deleteAction === 'reassign' ? reassignTarget : undefined,
        });
      } else {
        await maintenancePlansAPI.deletePlan(deleteTarget.id);
      }
      toast.success(`"${deleteTarget.name}" has been permanently deleted`);
      setDeleteTarget(null);
      setDeleteAction('remove');
      setReassignTarget('');
      await loadPlans();
      await loadEnrollments();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to delete plan');
    } finally { setDeleting(false); }
  };

  const openDeleteDialog = (plan) => {
    setDeleteTarget(plan);
    setDeleteAction('remove');
    setReassignTarget('');
  };

  const handleEnrollmentAction = async (enrollmentId, newStatus) => {
    const labels = { paused: 'Pause', canceled: 'Cancel', active: 'Resume' };
    if (newStatus === 'canceled' && !window.confirm('Cancel this enrollment? No further invoices or jobs will be generated.')) return;
    try {
      await maintenancePlansAPI.updateEnrollment(enrollmentId, { status: newStatus });
      toast.success(`Enrollment ${labels[newStatus].toLowerCase()}d`);
      await loadEnrollments();
      await loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update enrollment');
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="maintenance-plans-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Maintenance Plans</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Recurring service plans for your customers</p>
          </div>
          <Button onClick={() => { setEditingPlan(null); setDrawerOpen(true); }} data-testid="create-plan-btn" className="hidden md:inline-flex">
            <Plus className="w-4 h-4 mr-2" /> New Plan
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="plans" data-testid="tab-plans">Plans ({plans.length})</TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments ({enrollments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="mt-4">
            {plans.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Wrench className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-muted-foreground font-medium">No plans yet</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">Create your first maintenance plan to start enrolling customers.</p>
                  <Button className="mt-4" onClick={() => { setEditingPlan(null); setDrawerOpen(true); }} data-testid="create-first-plan-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create Plan
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {plans.map(p => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    onEdit={(plan) => { setEditingPlan(plan); setDrawerOpen(true); }}
                    onViewEnrollments={(plan) => { setActiveTab('enrollments'); }}
                    onDelete={openDeleteDialog}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="enrollments" className="mt-4">
            {enrollments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-muted-foreground font-medium">No enrollments yet</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">Enroll customers from their profile page.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  {enrollments.map(e => (
                    <EnrollmentRow key={e.id} enrollment={e} onAction={handleEnrollmentAction} />
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <MobileFAB onClick={() => { setEditingPlan(null); setDrawerOpen(true); }} data-testid="mobile-fab-plan" />

      <AppDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title={editingPlan ? 'Edit Plan' : 'New Maintenance Plan'}>
        <PlanForm plan={editingPlan} onSave={handleSavePlan} onClose={() => setDrawerOpen(false)} saving={saving} />
      </AppDrawer>

      {/* Delete Plan Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" /> Delete Plan
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {deleteTarget && (
                  <>
                    <p>
                      Permanently delete <span className="font-semibold text-foreground">{deleteTarget.name}</span>?
                      This action cannot be undone.
                    </p>

                    {deleteTarget.enrollment_count > 0 ? (
                      <>
                        <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            This plan has <span className="font-bold">{deleteTarget.enrollment_count} enrolled customer{deleteTarget.enrollment_count !== 1 ? 's' : ''}</span>.
                            Choose how to handle their enrollments before deleting.
                          </p>
                        </div>

                        <div className="space-y-2">
                          <button
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${deleteAction === 'remove' ? 'border-destructive bg-destructive/5 ring-1 ring-destructive/20' : 'border-border hover:border-destructive/30'}`}
                            onClick={() => setDeleteAction('remove')}
                            data-testid="delete-action-remove"
                          >
                            <UserMinus className={`w-5 h-5 shrink-0 ${deleteAction === 'remove' ? 'text-destructive' : 'text-muted-foreground'}`} />
                            <div>
                              <p className="text-sm font-medium">Remove enrollments</p>
                              <p className="text-xs text-muted-foreground">All customers will be unenrolled. They will have no maintenance plan.</p>
                            </div>
                          </button>
                          <button
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${deleteAction === 'reassign' ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-border hover:border-primary/30'}`}
                            onClick={() => setDeleteAction('reassign')}
                            data-testid="delete-action-reassign"
                          >
                            <ArrowRightLeft className={`w-5 h-5 shrink-0 ${deleteAction === 'reassign' ? 'text-primary' : 'text-muted-foreground'}`} />
                            <div>
                              <p className="text-sm font-medium">Reassign to another plan</p>
                              <p className="text-xs text-muted-foreground">Move all enrolled customers to a different plan before deleting.</p>
                            </div>
                          </button>
                        </div>

                        {deleteAction === 'reassign' && (
                          <div>
                            <Label className="text-sm font-medium">Move customers to:</Label>
                            <Select value={reassignTarget} onValueChange={setReassignTarget}>
                              <SelectTrigger data-testid="reassign-plan-select">
                                <SelectValue placeholder="Select destination plan..." />
                              </SelectTrigger>
                              <SelectContent>
                                {plans.filter(p => p.id !== deleteTarget.id).map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} — ${p.price.toFixed(2)}/{FREQ_LABELS[p.billing_frequency]?.toLowerCase()}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">This plan has no enrolled customers. It can be safely deleted.</p>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-plan">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePlan}
              disabled={deleting || (deleteTarget?.enrollment_count > 0 && deleteAction === 'reassign' && !reassignTarget)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-plan"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {deleteTarget?.enrollment_count > 0
                ? (deleteAction === 'remove' ? `Remove ${deleteTarget.enrollment_count} & Delete` : 'Reassign & Delete')
                : 'Delete Plan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
